import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ToastProvider } from './components/Common/Toast';
import { BadgeCelebrationProvider } from './contexts/BadgeCelebrationContext';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/Common/OfflineBanner';
import Spinner from './components/Common/Spinner';
import './App.css';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const SMSConsentPage = lazy(() => import('./pages/SMSConsentPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const FacilityDetailPage = lazy(() => import('./pages/FacilityDetailPage'));
const CampsiteDetailPage = lazy(() => import('./pages/CampsiteDetailPage'));
const MonitoringPage = lazy(() => import('./pages/MonitoringPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));

// Protected Route wrapper - Only for routes that REQUIRE authentication to view
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    // Still loading auth state
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '18px',
        color: '#176842'
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    // Not authenticated - redirect to login
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <SubscriptionProvider>
        <ToastProvider>
        <BadgeCelebrationProvider>
          <ErrorBoundary>
            <Suspense fallback={<div className="page-loading"><Spinner size="large" /></div>}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/sms-consent" element={<SMSConsentPage />} />
                <Route path="/faq" element={<FAQPage />} />

                {/* Browseable routes */}
                <Route path="/explore" element={<ExplorePage />} />
                <Route path="/facility/:facilityId" element={<FacilityDetailPage />} />
                <Route path="/facility/:facilityId/campsite/:campsiteId" element={<CampsiteDetailPage />} />
                <Route path="/monitoring" element={<MonitoringPage />} />
                <Route path="/profile/:userId" element={<PublicProfilePage />} />

                {/* Subscription is publicly viewable (pricing info) */}
                <Route path="/subscription" element={<SubscriptionPage />} />

                {/* Protected routes */}
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
          <OfflineBanner />
        </BadgeCelebrationProvider>
        </ToastProvider>
      </SubscriptionProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}





export default App;
